<template>
  <div
    ref="parallaxContainerRef"
    v-editable="blok"
    class="FeatureList layout-container"
    :class="[desktopWidth('full'), blockPaddingTop('md'), blockPaddingBottom('md')]"
  >
    <div
      v-if="blok.headingTitle || blok.headingButtonText"
      class="flex flex-row flex-wrap justify-between items-center gap-8 mb-16 px-12 desk:px-0"
      :class="{'flex-col text-center' : blok.headingAlign === 'center'}"
    >
      <div class="type-headline-xl"> {{ blok.headingTitle }}</div>

      <Nuxt-link
        :to="$sbLinkFix(blok.headingButtonLink)"
        :target="$sbLinkTarget(blok.headingButtonLink)"
      >
        <div
          v-if="blok.headingButtonText"
          class="btn btn--primary"
          :class="{
            'btn--secondary': blok.headingButtonStyle === 'secondary',
            'btn--ghost': blok.headingButtonStyle === 'ghost',
            'btn--text': blok.headingButtonStyle === 'plain',
          }"
        >
          {{ blok.headingButtonText }}
        </div>
      </Nuxt-link>
    </div>
    <div v-if="blok.design === 'default'">
      <!--Swiper-->
      <div
        v-if="(blok.displaySlider && isMobile) || (featureCount > 3)"
        class="relative"
      >
        <client-only>
          <Swiper
            class="swiper-cover desk:!overflow-hidden !overflow-visible w-full desk:!pb-40 !pb-32"
            :modules="[SwiperNavigation, SwiperPagination, SwiperA11y]"
            :space-between="8"
            :pagination="{
              clickable: true,
              dynamicBullets: true,
            }"
            :navigation="{
              nextEl: `.swiper-button-next.swiper-for-blok-${blok._uid}`,
              prevEl: `.swiper-button-prev.swiper-for-blok-${blok._uid}`,
            }"

            :loop="false"
            :slides-per-view="1.1"
            :no-swiping="true"
            :breakpoints="{
              '1024': {
                slidesPerView: 3,
                spaceBetween: 12,
              }
            }"
          >
            <SwiperSlide
              v-for="(feature, index) in blok.content"
              :key="feature._uid"
              class="flex flex-col items-center mb-24 desk:mb-32 deskLarge:!overflow-hidden desk:cursor-grab desk:active:cursor-grabbing"
            >
              <div ref="imageContainer">
                <div
                  v-if="feature.imageOrBackgroundVideo && feature.imageOrBackgroundVideo.filename"
                  class="relative w-full h-full"
                  :class="{
                    'aspect-landscape' : blok.aspectRatio === 'aspect-landscape',
                    'aspect-square' : blok.aspectRatio === 'aspect-square',
                  }"
                >
                  <video
                    v-if="checkIfVideoFile(feature.imageOrBackgroundVideo.filename)"
                    width="w-full"
                    height="h-full"
                    class="w-full h-full object-cover"
                    loop
                    muted
                    autoplay
                    playsinline
                  >
                    <source :src="feature.imageOrBackgroundVideo.filename" type="video/mp4">
                  </video>
                  <nuxt-img
                    v-else-if="feature.imageOrBackgroundVideo && feature.imageOrBackgroundVideo.filename"
                    preset="standard"
                    loading="lazy"
                    sizes="sm:100vw desk:1260px"
                    class="w-full h-full object-cover"
                    :class="feature.focalPoint"
                    :src="feature.imageOrBackgroundVideo.filename"
                    :alt="feature.imageOrBackgroundVideo.alt"
                  />
                  <div v-if="blok.displayNumbers">
                    <div
                      class="absolute left-0 bottom-0 flex justify-center items-center
                  w-40 h-40 desk:w-56 desk:h-56
                  bg-bg-active text-text-inverse type-headline-xl"
                    >
                      {{ index + 1 }}
                    </div>
                  </div>
                </div>
                <div v-else>
                  <div v-if="blok.displayNumbers">
                    <div
                      class="flex justify-center items-center
                  w-40 h-40 desk:w-56 desk:h-56
                  bg-bg-active text-text-inverse type-headline-xl"
                    >
                      {{ index + 1 }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="pt-16 desk:pt-20">
                <div class="type-headline-xl mb-8">{{ feature.headline }}</div>
                <div class="type-base text-text-subtle wysiwyg mb-16 desk:mb-20">
                  <ExtendedWysiwyg :content="feature.text" />
                </div>
                <Nuxt-link
                  :to="$sbLinkFix(feature.buttonLink)"
                  :target="$sbLinkTarget(feature.buttonLink)"
                >
                  <div
                    v-if="feature.buttonText"
                    class="btn btn--primary"
                    :class="{
                      'btn--secondary': feature.buttonStyle === 'secondary',
                      'btn--ghost': feature.buttonStyle === 'ghost',
                      'btn--text': feature.buttonStyle === 'plain',
                    }"
                  >
                    {{ feature.buttonText }}
                  </div>
                </Nuxt-link>
              </div>
            </SwiperSlide>
          </Swiper>
        </client-only>
        <div class="hidden desk:block">
          <div
            class="swiper-button-prev"
            :class="{[`swiper-for-blok-${blok._uid}`]: true}"
          />
          <div
            class="swiper-button-next"
            :class="{[`swiper-for-blok-${blok._uid}`]: true}"
          />
        </div>
      </div>
      <!--Default-->
      <div v-else class="flex flex-col desk:flex-row gap-32 desk:gap-12">
        <div
          v-for="(feature, index) in blok.content"
          :key="feature._uid"
          class="flex w-full flex-col"
        >
          <div ref="imageContainer">
            <div
              v-if="feature.imageOrBackgroundVideo && feature.imageOrBackgroundVideo.filename"
              class="relative w-full h-full"
              :class="{
                'aspect-landscape' : blok.aspectRatio === 'aspect-landscape',
                'aspect-square' : blok.aspectRatio === 'aspect-square',
              }"
            >
              <video
                v-if="checkIfVideoFile(feature.imageOrBackgroundVideo.filename)"
                width="w-full"
                height="h-full"
                class="w-full h-full object-cover"
                loop
                muted
                autoplay
                playsinline
              >
                <source :src="feature.imageOrBackgroundVideo.filename" type="video/mp4">
              </video>
              <nuxt-img
                v-else-if="feature.imageOrBackgroundVideo && feature.imageOrBackgroundVideo.filename"
                preset="standard"
                loading="lazy"
                sizes="sm:100vw desk:1260px"
                class="w-full h-full object-cover"
                :class="feature.focalPoint"
                :src="feature.imageOrBackgroundVideo.filename"
                :alt="feature.imageOrBackgroundVideo.alt"
              />
              <div v-if="blok.displayNumbers">
                <div
                  class="absolute left-0 bottom-0 flex justify-center items-center
                  w-40 h-40 desk:w-56 desk:h-56
                  bg-bg-active text-text-inverse type-headline-xl"
                >
                  {{ index + 1 }}
                </div>
              </div>
            </div>
            <div v-else>
              <div v-if="blok.displayNumbers">
                <div
                  class="flex justify-center items-center
                  w-40 h-40 desk:w-56 desk:h-56
                  bg-bg-active text-text-inverse type-headline-xl"
                >
                  {{ index + 1 }}
                </div>
              </div>
            </div>
          </div>

          <div class="pt-16 desk:pt-20">
            <div class="type-headline-xl mb-8">{{ feature.headline }}</div>
            <div class="type-base text-text-subtle wysiwyg mb-16 desk:mb-20">
              <ExtendedWysiwyg :content="feature.text" />
            </div>
            <Nuxt-link
              :to="$sbLinkFix(feature.buttonLink)"
              :target="$sbLinkTarget(feature.buttonLink)"
            >
              <div
                v-if="feature.buttonText"
                class="btn btn--primary"
                :class="{
                  'btn--secondary': feature.buttonStyle === 'secondary',
                  'btn--ghost': feature.buttonStyle === 'ghost',
                  'btn--text': feature.buttonStyle === 'plain',
                }"
              >
                {{ feature.buttonText }}
              </div>
            </Nuxt-link>
          </div>
        </div>
      </div>
    </div>
    <!--Stacked-->
    <div v-else>
      <div class="grid grid-cols-1 desk:grid-cols-2 gap-64">
        <div class="flex flex-col gap-48 desk:gap-64">
          <div
            v-for="(feature, index) in blok.content"
            :key="feature._uid"
            class="flex flex-row gap-24 desk:gap-48"
          >
            <div
              v-if="blok.displayNumbers"
              class="flex justify-center items-center shrink-0
                  w-40 h-40 desk:w-56 desk:h-56
                  bg-bg-active text-text-inverse type-headline-xl"
            >
              {{ index + 1 }}
            </div>
            <div class="flex flex-col gap-8">
              <div class="type-headline-3xl min-h-40 desk:min-h-56">
                {{ feature.headline }}
              </div>
              <div class="type-base wysiwyg mb-16 desk:mb-20">
                <ExtendedWysiwyg :content="feature.text" />
              </div>
              <Nuxt-link
                :to="$sbLinkFix(feature.buttonLink)"
                :target="$sbLinkTarget(feature.buttonLink)"
              >
                <div
                  v-if="feature.buttonText"
                  class="btn btn--primary"
                  :class="{
                    'btn--secondary': feature.buttonStyle === 'secondary',
                    'btn--ghost': feature.buttonStyle === 'ghost',
                    'btn--text': feature.buttonStyle === 'plain',
                  }"
                >
                  {{ feature.buttonText }}
                </div>
              </Nuxt-link>
            </div>
          </div>
        </div>
        <div
          v-if="blok.stackedVisual && blok.stackedVisual.filename"
          class="overflow-hidden"
          :class="{
            'aspect-landscape' : blok.aspectRatio === 'aspect-landscape',
            'aspect-square' : blok.aspectRatio === 'aspect-square',
          }"
        >
          <video
            v-if="checkIfVideoFile(blok.stackedVisual.filename)"
            :style="blok.parallaxEffect ? { transform: `translateY(${offset}px) scale(1.2)` } : {}"
            width="w-full"
            height="h-full"
            class="w-full h-full object-cover transition-all duration-[50ms]"
            loop
            muted
            autoplay
            playsinline
          >
            <source :src="blok.stackedVisual.filename" type="video/mp4">
          </video>
          <nuxt-img
            v-else-if="blok.stackedVisual && blok.stackedVisual.filename"
            :style="blok.parallaxEffect ? { transform: `translateY(${offset}px) scale(1.2)` } : {}"
            preset="standard"
            loading="lazy"
            sizes="sm:100vw desk:1260px"
            class="w-full h-full object-cover transition-all duration-[50ms]"
            :class="blok.stackedFocalPoint"
            :src="blok.stackedVisual.filename"
            :alt="blok.stackedVisual.alt"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  DatasourceObjectPosition,
  StoryBlokAsset,
  StoryBlokLink,
  StoryBlokPropBase
} from '~/constants/types/storyblok';
import useVisualAppearancePlugin from '~/composeables/useVisualAppearancePlugin';
import { ISbRichtext } from 'storyblok-js-client';
import { checkIfVideoFile } from '~/util/imageUtil';

import { useUiStore } from '~/store/ui';
import { storeToRefs } from 'pinia';
import { useElementBounding } from '@vueuse/core';
import { computed, ref } from 'vue';

const uiStore = useUiStore();
const { isMobile } = storeToRefs(uiStore);

type Content = StoryBlokPropBase & {
  headline: string;
  text: ISbRichtext;
  imageOrBackgroundVideo: StoryBlokAsset;
  focalPoint: DatasourceObjectPosition;
  buttonText: string;
  buttonLink: StoryBlokLink;
  buttonStyle: 'primary' | 'secondary' | 'ghost' | 'plain';
}

type Props = StoryBlokPropBase & {
  content: Content[];
  design: 'default' | 'stacked';
  displaySlider: boolean;
  displayNumbers: boolean;
  aspectRatio: 'aspect-landscape' | 'aspect-square';
  stackedVisual: StoryBlokAsset;
  stackedFocalPoint: DatasourceObjectPosition;
  parallaxEffect: boolean;
  // Heading fields
  headingTitle: string;
  headingButtonText: string;
  headingButtonLink: StoryBlokLink;
  headingButtonStyle: 'primary' | 'secondary' | 'ghost' | 'plain';
  headingAlign: 'left' | 'center';
}

const props = defineProps<{
  blok: Props,
}>();

const { desktopWidth, blockPaddingTop, blockPaddingBottom } = useVisualAppearancePlugin(props);

const imageContainer = ref<HTMLDivElement>();
const imageContainerHeight = ref(0);

onMounted(() => {
  imageContainerHeight.value = imageContainer.value && imageContainer.value.clientHeight || 0;
});

const featureCount = computed(() => props.blok.content.length);

const parallaxContainerRef = useTemplateRef('parallaxContainerRef');
const { top } = useElementBounding(parallaxContainerRef);

const windowHeight = ref(0);
onMounted(()=> {
  windowHeight.value = window.innerHeight;
});

const progress = computed(() => {
  const start = windowHeight.value;
  const end = -400;
  return Math.max(0, Math.min((start - top.value) / (start - end), 1));
});

const offset = computed(() => progress.value * 80);

</script>

<style lang="postcss">
.swiper-button-next {
  @apply !-right-16;
}

.swiper-button-prev {
  @apply !-left-16;
}
</style>
